import type { NavigationExtras } from '@angular/router';

/** URL segments under `/create-agent/` — order matches wizard steps 1–5 */
export const CREATE_AGENT_STEPS = [
  'agent-details',
  'training-questions',
  'simulate-agent',
  'evaluate',
  'deploy-retain'
] as const;

export type CreateAgentStepSegment = (typeof CREATE_AGENT_STEPS)[number];

export const CREATE_AGENT_BASE = '/create-agent';

/** Query param for the public agent UUID (preferred over legacy `agentId`). */
export const CREATE_AGENT_AGENT_QUERY_PARAM = 'agent';

/** Navigate to step by 0-based index (0 = agent-details, …, 4 = deploy-retain) */
export function createAgentStepLink(stepIndex: number): [string, (typeof CREATE_AGENT_STEPS)[number]] {
  const seg = CREATE_AGENT_STEPS[stepIndex];
  if (seg === undefined) {
    throw new RangeError(`Invalid create-agent step index: ${stepIndex}`);
  }
  return [CREATE_AGENT_BASE, seg];
}

/**
 * Router target for a wizard step, with optional `?agent=<uuid>` so refresh and deep links work.
 * Pass `null` / omit `agentKey` to clear the agent query param (e.g. brand-new draft).
 */
export function navigateCreateAgentStep(
  stepIndex: number,
  agentKey?: string | null,
): { commands: string[]; extras: NavigationExtras } {
  const [base, seg] = createAgentStepLink(stepIndex);
  const k = (agentKey ?? '').trim();
  // Drop legacy `agentId` so the URL only carries the public `agent` UUID.
  const extras: NavigationExtras = k
    ? { queryParams: { [CREATE_AGENT_AGENT_QUERY_PARAM]: k, agentId: null } }
    : { queryParams: { [CREATE_AGENT_AGENT_QUERY_PARAM]: null, agentId: null } };
  return { commands: [base, seg], extras };
}
